/**
 * Programmer / modifier une évaluation, et la noter :
 * - `EvaluationFormSheet` : nom, date, matière, SKILLCARDS, coefficient, et la
 *   note si l'éval est passée (aujourd'hui compris).
 * - `GradeSheet` : saisie rapide de la note d'une éval déjà programmée.
 * - `ScoreFields` : note + barème (5 / 10 / 20 / 100 / autre) + coefficient.
 */

import { useId, useState } from 'react';
import { EvaluationPhotos } from './evaluation-photos';
import { useStudentEvaluationPhotos } from '@/lib/evaluations/photos';
import { Trash2 } from 'lucide-react';
import { Button, Input, MathText, cn } from '@jeraisonne/ui';
import {
  EVALUATION_MAX_SCORE_PRESETS,
  localDateKey,
  type EvaluationCardRef,
  type StudentEvaluation,
} from '@/lib/evaluations/grades';
import {
  gradedDraftIssue,
  numberToField,
  parseEvaluationDraft,
  parseScoreDraft,
  type ScoreDraft,
} from '@/lib/evaluations/form';
import {
  evaluationErrorMessage,
  useDeleteEvaluation,
  useGradeEvaluation,
  useSaveEvaluation,
} from '@/lib/evaluations/api';
import { EvaluationCardsPicker, type CardCandidate } from './evaluation-cards-picker';
import { Sheet } from './sheet';

const TITLE_SUGGESTIONS = ['Contrôle', 'Interro', 'DS', 'Devoir maison', 'Bac blanc'];

export function ScoreFields({
  value,
  onChange,
  showScore = true,
}: {
  value: ScoreDraft;
  onChange: (next: ScoreDraft) => void;
  showScore?: boolean;
}) {
  const id = useId();
  const presetValue = EVALUATION_MAX_SCORE_PRESETS.find((p) => String(p) === value.maxScore);
  const [custom, setCustom] = useState(presetValue == null);

  return (
    <div className="space-y-3">
      {showScore && (
        <div className="space-y-1">
          <label htmlFor={`${id}-score`} className="text-ink-700 block text-sm font-semibold">
            Ma note
          </label>
          <div className="flex items-center gap-2">
            <Input
              id={`${id}-score`}
              inputMode="decimal"
              placeholder="ex. 13,5"
              value={value.score}
              onChange={(e) => onChange({ ...value, score: e.target.value })}
              className="w-28"
            />
            <span className="text-ink-500 text-sm">sur {value.maxScore || '…'}</span>
          </div>
        </div>
      )}
      <fieldset className="space-y-1">
        <legend className="text-ink-700 text-sm font-semibold">Barème</legend>
        <div className="flex flex-wrap gap-2">
          {EVALUATION_MAX_SCORE_PRESETS.map((p) => {
            const on = !custom && value.maxScore === String(p);
            return (
              <button
                key={p}
                type="button"
                aria-pressed={on}
                onClick={() => {
                  setCustom(false);
                  onChange({ ...value, maxScore: String(p) });
                }}
                className={cn(
                  'min-h-[2.75rem] min-w-[2.75rem] rounded-full border px-4 text-sm font-semibold active:scale-[0.98]',
                  on
                    ? 'border-primary-600 bg-primary-600 text-white'
                    : 'border-ink-200 text-ink-700',
                )}
              >
                /{p}
              </button>
            );
          })}
          <button
            type="button"
            aria-pressed={custom}
            onClick={() => setCustom(true)}
            className={cn(
              'min-h-[2.75rem] rounded-full border px-4 text-sm font-semibold active:scale-[0.98]',
              custom
                ? 'border-primary-600 bg-primary-600 text-white'
                : 'border-ink-200 text-ink-700',
            )}
          >
            Autre
          </button>
        </div>
        {custom && (
          <Input
            aria-label="Barème personnalisé"
            inputMode="decimal"
            placeholder="ex. 40"
            value={value.maxScore}
            onChange={(e) => onChange({ ...value, maxScore: e.target.value })}
            className="mt-2 w-28"
          />
        )}
      </fieldset>
      <div className="space-y-1">
        <label htmlFor={`${id}-coef`} className="text-ink-700 block text-sm font-semibold">
          Coefficient
        </label>
        <Input
          id={`${id}-coef`}
          inputMode="decimal"
          placeholder="1"
          value={value.coefficient}
          onChange={(e) => onChange({ ...value, coefficient: e.target.value })}
          className="w-28"
        />
      </div>
    </div>
  );
}

export function EvaluationFormSheet({
  userId,
  evaluation,
  candidates,
  subjectSuggestions,
  onClose,
  hasTutor = false,
  mode = 'plan',
}: {
  userId: string;
  /** Absent = nouvelle éval. */
  evaluation?: StudentEvaluation | null;
  candidates: readonly CardCandidate[];
  subjectSuggestions: readonly string[];
  onClose: () => void;
  /** L'élève a un prof lié : il peut lui envoyer copie et énoncé (0333). */
  hasTutor?: boolean;
  /**
   * `plan` : programmer une éval (note plus tard). `graded` : ajouter une note
   * DÉJÀ reçue — date passée ou du jour, note obligatoire, saisie en premier.
   */
  mode?: 'plan' | 'graded';
}) {
  const id = useId();
  const save = useSaveEvaluation(userId);
  const remove = useDeleteEvaluation(userId);
  const [title, setTitle] = useState(evaluation?.title ?? '');
  const [subject, setSubject] = useState(evaluation?.subject ?? subjectSuggestions[0] ?? '');
  const [evalDate, setEvalDate] = useState(evaluation?.eval_date ?? localDateKey());
  const [note, setNote] = useState(evaluation?.note ?? '');
  const [cards, setCards] = useState<EvaluationCardRef[]>(evaluation?.cards ?? []);
  const [score, setScore] = useState<ScoreDraft>({
    score: numberToField(evaluation?.score),
    maxScore: numberToField(evaluation?.max_score ?? 20),
    coefficient: numberToField(evaluation?.coefficient ?? 1),
  });
  const [error, setError] = useState<string | null>(null);
  const today = localDateKey();
  const graded = mode === 'graded' && !evaluation;
  const isPast = evalDate !== '' && evalDate <= today;

  async function submit() {
    if (graded) {
      const issue = gradedDraftIssue(evalDate, score.score, today);
      if (issue) {
        setError(issue);
        return;
      }
    }
    const parsed = parseEvaluationDraft({ title, subject, evalDate, note, ...score });
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    setError(null);
    try {
      await save.mutateAsync({
        id: evaluation?.id,
        title: parsed.title,
        subject: parsed.subject,
        eval_date: parsed.eval_date,
        score: parsed.score,
        max_score: parsed.max_score,
        coefficient: parsed.coefficient,
        note: parsed.note,
        cards,
      });
      onClose();
    } catch (e) {
      setError(evaluationErrorMessage(e instanceof Error ? e.message : ''));
    }
  }

  async function destroy() {
    if (!evaluation) return;
    if (!window.confirm('Supprimer cette éval et sa note ?')) return;
    try {
      await remove.mutateAsync(evaluation.id);
      onClose();
    } catch (e) {
      setError(evaluationErrorMessage(e instanceof Error ? e.message : ''));
    }
  }

  const scoreBlock = (
    <div className="border-ink-100 ocean:border-white/10 space-y-2 rounded-2xl border p-3">
      {graded ? (
        <p className="text-ink-700 text-sm font-semibold">Ta note</p>
      ) : isPast ? (
        <p className="text-ink-600 text-sm">Tu as déjà ta note ? Saisis-la (sinon plus tard).</p>
      ) : (
        <p className="text-ink-600 text-sm">
          Tu saisiras ta note après l’éval. Indique déjà le barème et le coefficient si tu les
          connais.
        </p>
      )}
      <ScoreFields value={score} onChange={setScore} showScore={isPast} />
    </div>
  );

  return (
    <Sheet
      title={
        evaluation ? 'Modifier l’éval' : graded ? 'Ajouter une note reçue' : 'Programmer une éval'
      }
      onClose={onClose}
      footer={
        <div className="flex flex-col gap-2">
          {error && (
            <p role="alert" className="text-error-600 text-sm">
              {error}
            </p>
          )}
          <Button fullWidth loading={save.isPending} onClick={() => void submit()}>
            Enregistrer
          </Button>
        </div>
      }
    >
      <div className="space-y-1">
        <label htmlFor={`${id}-title`} className="text-ink-700 block text-sm font-semibold">
          Nom de l’éval
        </label>
        <Input
          id={`${id}-title`}
          placeholder="ex. Contrôle Pythagore"
          maxLength={120}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        {!title && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {TITLE_SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setTitle(`${s} `)}
                className="border-ink-200 text-ink-700 min-h-[2.75rem] rounded-full border px-3 text-sm active:scale-[0.98]"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="min-w-0 space-y-1">
          <label htmlFor={`${id}-date`} className="text-ink-700 block text-sm font-semibold">
            Date
          </label>
          <Input
            id={`${id}-date`}
            type="date"
            max={graded ? today : undefined}
            value={evalDate}
            onChange={(e) => setEvalDate(e.target.value)}
            // iOS : le champ date natif impose une largeur minimale qui
            // dépassait de la fenêtre (le formulaire glissait horizontalement).
            className="w-full min-w-0 appearance-none"
          />
        </div>
        <div className="min-w-0 space-y-1">
          <label htmlFor={`${id}-subject`} className="text-ink-700 block text-sm font-semibold">
            Matière
          </label>
          <Input
            id={`${id}-subject`}
            list={`${id}-subjects`}
            placeholder="ex. Mathématiques"
            maxLength={60}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          <datalist id={`${id}-subjects`}>
            {subjectSuggestions.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </div>
      </div>

      {graded && scoreBlock}

      <EvaluationCardsPicker candidates={candidates} value={cards} onChange={setCards} />

      {!graded && scoreBlock}

      <div className="space-y-1">
        <label htmlFor={`${id}-note`} className="text-ink-700 block text-sm font-semibold">
          Remarque (facultatif)
        </label>
        <textarea
          id={`${id}-note`}
          rows={2}
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="ex. Chapitre 4, exercices 12 à 18"
          className="border-ink-200 text-ink-900 focus:border-primary-500 w-full rounded-[10px] border bg-white px-4 py-2 text-[15px] focus:outline-none"
        />
      </div>

      {evaluation && hasTutor && (
        <EvaluationPhotosSection userId={userId} evaluationId={evaluation.id} />
      )}

      {evaluation && (
        <Button
          variant="ghost"
          fullWidth
          loading={remove.isPending}
          onClick={() => void destroy()}
          className="text-error-600"
        >
          <Trash2 className="h-4 w-4" aria-hidden /> Supprimer l’éval
        </Button>
      )}
    </Sheet>
  );
}

export function GradeSheet({
  userId,
  evaluation,
  onClose,
  hasTutor = false,
}: {
  userId: string;
  evaluation: StudentEvaluation;
  onClose: () => void;
  /** L'élève a un prof lié : il peut lui envoyer copie et énoncé (0333). */
  hasTutor?: boolean;
}) {
  const grade = useGradeEvaluation(userId);
  const [draft, setDraft] = useState<ScoreDraft>({
    score: numberToField(evaluation.score),
    maxScore: numberToField(evaluation.max_score),
    coefficient: numberToField(evaluation.coefficient),
  });
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const parsed = parseScoreDraft(draft, true);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    setError(null);
    try {
      await grade.mutateAsync({
        id: evaluation.id,
        score: parsed.score,
        max_score: parsed.max_score,
        coefficient: parsed.coefficient,
      });
      onClose();
    } catch (e) {
      setError(evaluationErrorMessage(e instanceof Error ? e.message : ''));
    }
  }

  return (
    <Sheet
      title="Ma note"
      onClose={onClose}
      footer={
        <div className="flex flex-col gap-2">
          {error && (
            <p role="alert" className="text-error-600 text-sm">
              {error}
            </p>
          )}
          <Button fullWidth loading={grade.isPending} onClick={() => void submit()}>
            Enregistrer ma note
          </Button>
        </div>
      }
    >
      <p className="text-ink-900 font-semibold">
        <MathText>{evaluation.title}</MathText>
      </p>
      <ScoreFields value={draft} onChange={setDraft} />
      {hasTutor && <EvaluationPhotosSection userId={userId} evaluationId={evaluation.id} />}
    </Sheet>
  );
}

/** « Envoie ta copie à ton prof » : photos de la copie et de l'énoncé (0333). */
export function EvaluationPhotosSection({
  userId,
  evaluationId,
}: {
  userId: string;
  evaluationId: string;
}) {
  const photos = useStudentEvaluationPhotos(userId);
  return (
    <div className="border-ink-100 mt-4 space-y-2 border-t pt-4">
      <p className="text-ink-900 text-sm font-semibold">Envoie ta copie à ton prof</p>
      <p className="text-ink-500 text-xs">
        Photo de ta copie corrigée et de l’énoncé : ton prof les voit sur ta fiche.
      </p>
      <EvaluationPhotos
        studentId={userId}
        evaluationId={evaluationId}
        photos={(photos.data ?? []).filter((p) => p.evaluation_id === evaluationId)}
      />
    </div>
  );
}
